/**
 * Reporter central de errores del frontend.
 *
 * Objetivo: UN solo punto por donde pasan todos los errores (errores de
 * componentes Vue, promesas sin capturar, errores globales de window y fallos
 * HTTP del BaseService). Enriquecen el evento con contexto, se sanitiza la PII
 * y se entrega a un "sink" intercambiable.
 *
 * Destinos:
 *   - Produccion  -> ApiSink: POST al backend propio via `logFrontendService`
 *                    (de ahi a stdout -> Promtail -> Loki -> Grafana).
 *   - Desarrollo  -> ConsoleSink.
 *
 * Todo queda dentro de la infraestructura propia; no se usan servicios externos.
 * `configurarSink(miSink)` queda como punto de extension por si algun dia hace
 * falta otro destino.
 */
import type { App } from 'vue'
import type { IUserLoged } from '@/modules/Autenticacion/interfaces/IFathersAuthItem'
import { BaseService } from '@/modules/Core/services/BaseService'
import { sanitizarPII, sanitizarTexto } from './sanitizarPII'
import type {
  IErrorContexto,
  IErrorEvent,
  IErrorNivel,
  IErrorOrigen,
  IErrorSink
} from './IErrorEvent'

const isProd = import.meta.env.PROD
const VERSION = (import.meta.env.VITE_APP_VERSION as string) || 'dev'

// --- Contexto ---------------------------------------------------------------

function usuarioActual(): Partial<Pick<IUserLoged, 'id' | 'entidad' | 'username'>> {
  try {
    // El usuario ya viene decodificado en la cookie 'user' (lo setea el login),
    // igual que lo consume el resto de la app. Solo id, username y entidad;
    // nunca nombre/email/documento.
    const u = BaseService.cookies?.get('user') as IUserLoged | null
    if (!u) return {}
    return { id: u.id, entidad: u.entidad, username: u.username }
  } catch {
    return {}
  }
}

function construirContexto(): IErrorContexto {
  const u = usuarioActual()
  return {
    url: sanitizarTexto(window.location.href),
    ruta: BaseService.router?.currentRoute?.value?.name?.toString(),
    usuarioId: u.id,
    usuario: u.username,
    entidad: u.entidad,
    version: VERSION,
    userAgent: navigator.userAgent
  }
}

// --- Sinks ------------------------------------------------------------------

class ConsoleSink implements IErrorSink {
  enviar(evento: IErrorEvent): void {
    // Agrupado y legible; solo en dev por defecto
    console.groupCollapsed(
      `%c[error:${evento.origen}] ${evento.mensaje}`,
      'color:#fff;background:#c0392b;padding:2px 6px;border-radius:4px'
    )
    console.log('contexto', evento.contexto)
    if (evento.extra) console.log('extra', evento.extra)
    if (evento.stack) console.log(evento.stack)
    console.groupEnd()
  }
}

/**
 * Envia los eventos al backend propio via `logFrontendService`, igual que
 * cualquier otro servicio del proyecto (la URL sale de VITE_API_URL a traves de
 * BaseService, no hace falta una variable de entorno aparte).
 *
 * El import es DINAMICO a proposito: BaseService importa este modulo para
 * reportar los fallos HTTP y LogFrontendService extiende BaseService. Con un
 * import estatico se cerraria el ciclo y la clase quedaria sin inicializar (TDZ)
 * al cargar la app.
 */
class ApiSink implements IErrorSink {
  async enviar(evento: IErrorEvent): Promise<void> {
    try {
      const { logFrontendService } = await import('@/services')
      await logFrontendService.registrar(evento)
    } catch {
      /* nunca dejar que el reporte de un error genere otro error */
    }
  }
}

// Sink por defecto: en prod intenta HTTP (no-op si no hay endpoint), en dev consola.
let sinkActual: IErrorSink = isProd ? new ApiSink() : new ConsoleSink()

/**
 * Reemplaza el destino de los errores. Llamar en el arranque para enchufar
 * Sentry/GlitchTip u otro colector sin tocar la captura.
 */
export function configurarSink(sink: IErrorSink): void {
  sinkActual = sink
}

// --- Anti-flood (dedupe simple por ventana de tiempo) -----------------------

const vistos = new Map<string, number>()
const VENTANA_MS = 10_000

function esDuplicado(clave: string): boolean {
  const ahora = Date.now()
  const previo = vistos.get(clave)
  vistos.set(clave, ahora)
  if (previo && ahora - previo < VENTANA_MS) return true
  // limpieza barata
  if (vistos.size > 200) vistos.clear()
  return false
}

// --- API publica ------------------------------------------------------------

// Ruido del navegador, no son fallos de la app.
// ponytail: lista literal; si crece, pasar a regex
const IGNORADOS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded'
]

// Preline no valida `event.key` en su listener global de teclado; los eventos
// sinteticos (autocompletado, lectores de codigo de barras, extensiones) lo
// hacen reventar sin efecto para el usuario.
// ponytail: filtrado por stack; quitar cuando preline lo arregle
const IGNORADOS_STACK = ['isAllowedKeybinding']

interface OpcionesReporte {
  origen?: IErrorOrigen
  nivel?: IErrorNivel
  extra?: Record<string, unknown>
}

/**
 * Punto de entrada unico. Recibe cualquier error, lo enriquece, sanitiza y
 * entrega al sink. Nunca lanza.
 */
export function reportarError(error: unknown, opciones: OpcionesReporte = {}): void {
  try {
    const err = error instanceof Error ? error : undefined
    const mensaje = sanitizarTexto(
      err?.message ?? (typeof error === 'string' ? error : String(error))
    )
    if (IGNORADOS.some((m) => mensaje.includes(m))) return
    if (err?.stack && IGNORADOS_STACK.some((m) => err.stack!.includes(m))) return

    const clave = `${opciones.origen ?? 'manual'}:${mensaje}`
    if (esDuplicado(clave)) return

    const evento: IErrorEvent = {
      mensaje,
      stack: err?.stack ? sanitizarTexto(err.stack) : undefined,
      origen: opciones.origen ?? 'manual',
      nivel: opciones.nivel ?? 'error',
      contexto: construirContexto(),
      extra: opciones.extra
        ? (sanitizarPII(opciones.extra) as Record<string, unknown>)
        : undefined,
      timestamp: new Date().toISOString()
    }
    void sinkActual.enviar(evento)
  } catch {
    /* el reporter jamas debe romper la app */
  }
}

/**
 * Engancha la captura global. Llamar una vez en main.ts despues de crear la app.
 */
export function initErrorReporter(app: App): void {
  // Errores de componentes Vue (render, watchers, lifecycle)
  const prev = app.config.errorHandler
  app.config.errorHandler = (err, instance, info) => {
    reportarError(err, { origen: 'vue', extra: { info } })
    if (typeof prev === 'function') prev(err, instance, info)
  }

  // Promesas rechazadas sin catch
  window.addEventListener('unhandledrejection', (e) => {
    reportarError(e.reason, { origen: 'promesa' })
  })

  // Errores globales de JS
  window.addEventListener('error', (e) => {
    reportarError(e.error ?? e.message, {
      origen: 'window',
      extra: { archivo: e.filename, linea: e.lineno, columna: e.colno }
    })
  })
}
